import { type SubmitHandler, useForm } from 'react-hook-form';
import {
  type GqlOptionsSharesSubscriber,
  type GqlVestedAndAllocated,
  type GqlIdentity,
} from '../../../../../../graphql/base-types.graphql';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Stack } from '@startuptools/ui/Stack';
import { Trans, useLingui } from '@lingui/react';
import { BigNumber } from 'bignumber.js';
import { type DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { StepOsrSubscribersDocument, useOssCreateMutation, useOssUpdateMutation } from './options-subscribers.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { shareClassToInput } from '../../../../../../helpers/share-class-to-input';

type FormValues = {
  quantity: string;
  email: string | null;
  phone: string | null;
};

export const OptionsSharesSubscriberForm = ({
  oss,
  osrId,
  identity,
  shareClassName,
  vestedAndAllocated,
  onClose,
}: {
  oss?: Pick<GqlOptionsSharesSubscriber, 'id' | 'quantity'> | null;
  osrId: string;
  identity: DisplayIdentity;
  shareClassName: string;
  vestedAndAllocated: (Pick<GqlVestedAndAllocated, 'subscribable' | 'shareClass'> & {
    identity: Pick<GqlIdentity, 'id'>;
  })[];

  onClose: () => void;
}) => {
  const { i18n } = useLingui();
  const companyId = useCompanyId();

  const { register, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: { quantity: oss?.quantity, email: identity.email, phone: identity.phone },
  });

  // eslint-disable-next-line sut/loading-mutation
  const [createOss] = useOssCreateMutation({ refetchQueries: [StepOsrSubscribersDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [updateOss] = useOssUpdateMutation({ refetchQueries: [StepOsrSubscribersDocument] });

  const selectedEntity = vestedAndAllocated.find(
    e => e.identity.id === identity.id && e.shareClass.name === shareClassName,
  );

  if (!selectedEntity) {
    throw new Error(`could not find vestedAndAllocated for ${identity.id}, ${identity.name}`);
  }

  const availableOptions = new BigNumber(selectedEntity.subscribable ?? 0).plus(oss?.quantity ?? 0).toString();

  const onSubmit: SubmitHandler<FormValues> = async values => {
    const update = {
      identityInput: identityToInput(identity, values.email, values.phone),
      quantity: values.quantity,
      shareClass: shareClassToInput(selectedEntity.shareClass),
    };
    if (!oss) {
      await createOss({
        variables: { companyId, input: { ...update, optionsSharesRegistrationId: osrId } },
      });
    } else {
      await updateOss({ variables: { companyId, input: { ...update, id: oss.id } } });
    }
    onClose();
  };

  const disabled = false;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack direction="column" spacing={2}>
        <FormControl>
          <FormLabel>
            <Trans id="Quantity" />
          </FormLabel>
          <Input
            {...register('quantity')}
            type="number"
            endDecorator={
              <Button
                variant="soft"
                color="neutral"
                onClick={() => {
                  setValue('quantity', availableOptions);
                }}
              >
                <NumberFormat variant="number" maximumFractionDigits={0} locale={i18n.locale}>
                  {availableOptions}
                </NumberFormat>
              </Button>
            }
            defaultValue={oss?.quantity ?? ''}
          />
        </FormControl>

        <FormControl>
          <FormLabel>
            <Trans id="Email" />
          </FormLabel>
          <Input
            {...register('email')}
            disabled={disabled}
            defaultValue={identity.email ?? ''}
            type="email"
            fullWidth
            required
          />
        </FormControl>

        <FormControl>
          <FormLabel>
            <Trans id="Phone" />
          </FormLabel>
          <Input {...register('phone')} disabled={disabled} fullWidth defaultValue={identity.phone ?? ''} />
        </FormControl>
        {/* {(createMutation.error || updateMutation.error) && <GenericError />} */}
        <Stack direction="row" gap={1} justifyContent="end" alignItems="center">
          <Button variant="outlined" onClick={onClose}>
            <Trans id="Cancel" />
          </Button>
          <Button type="submit" disabled={disabled}>
            <Trans id="Save" />
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
